import React, { useState, useEffect } from 'react';
import api from '../api';

const ProfileScreen = ({ user, onLogout, onUpdate }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProfile();
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgraded') === 'true') {
      setSuccess('Welcome to VERABRIEF Premium!');
      window.history.replaceState({}, '', '/profile');
    }
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/profile');
      const p = res.data;
      setProfile(p);
      setName(p.name || '');
    } catch (err) {
      setError('Failed to load profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      const res = await api.post('/subscriptions/create');
      window.location.href = res.data.url;
    } catch (err) {
      setError('Failed to start upgrade. Please try again.');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription? You will keep access until the end of your billing period.')) return;
    try {
      await api.post('/subscriptions/cancel');
      setSuccess('Subscription cancelled. Access continues until end of billing period.');
      setProfile({ ...profile, premium: false });
    } catch (err) {
      setError('Failed to cancel subscription.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.patch('/profile', {
        name,
      });
      setProfile({ ...profile, ...res.data.user });
      if (onUpdate) onUpdate(res.data.user);
      setSuccess('Profile updated.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const initial = (profile?.name || profile?.email || 'V').charAt(0).toUpperCase();

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div className="skeleton" style={{ height: 120, borderRadius: 16 }} />
          <div className="skeleton" style={{ height: 200, borderRadius: 16 }} />
          <div className="skeleton" style={{ height: 200, borderRadius: 16 }} />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Avatar + Identity */}
        <div style={styles.heroCard}>
          <div style={styles.avatar}>{initial}</div>
          <div style={styles.identity}>
            <div style={styles.displayName}>{profile?.name || profile?.email?.split('@')[0]}</div>
            <div style={styles.email}>{profile?.email}</div>
            <div style={styles.meta}>
              <span style={styles.memberSince}>Member since {profile?.member_since}</span>
              <span style={styles.dot}>·</span>
              <span style={styles.statPill}>{profile?.sources_count} sources</span>
              <span style={styles.dot}>·</span>
              <span style={styles.statPill}>{profile?.briefs_count} briefs</span>
            </div>
          </div>
          <div style={{
            ...styles.planBadge,
            ...(profile?.premium ? styles.planBadgePremium : styles.planBadgeFree)
          }}>
            {profile?.premium ? '★ PREMIUM' : 'FREE'}
          </div>
        </div>

        {/* Upgrade banner — free users only */}
        {!profile?.premium && (
          <div style={styles.upgradeCard}>
            <div style={styles.upgradeLeft}>
              <div style={styles.upgradeTitle}>Unlock ANIMA Premium</div>
              <div style={styles.upgradeSub}>
                Full ANIMA cross-source analysis, unlimited briefs, SMS delivery — $4.95/mo
              </div>
            </div>
            <button
              className="btn btn-primary"
              style={styles.upgradeBtn}
              onClick={handleUpgrade}
            >
              Upgrade
            </button>
          </div>
        )}

        {/* Cancel subscription — premium users only */}
        {profile?.premium && (
          <div style={styles.cancelCard}>
            <div>
              <div style={styles.cancelTitle}>VERABRIEF Premium</div>
              <div style={styles.cancelSub}>Your subscription renews monthly at $4.95</div>
            </div>
            <button
              className="btn btn-ghost"
              style={styles.cancelBtn}
              onClick={handleCancel}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Account settings */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Account</div>

          <div style={styles.field}>
            <label style={styles.label}>Display Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={profile?.email || ''}
              disabled
              style={{ ...styles.input, ...styles.inputDisabled }}
            />
          </div>
        </div>

        {/* Delivery preferences */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Brief Delivery</div>

          {profile?.premium ? (
            <div style={styles.deliveryStatus}>
              <div style={styles.deliveryStatusDot} />
              <div>
                <div style={styles.deliveryStatusTitle}>Delivered automatically every morning</div>
                <div style={styles.deliveryStatusSub}>Your brief arrives by email at 7:00 AM Central, generated fresh from your sources.</div>
              </div>
            </div>
          ) : (
            <div style={styles.deliveryStatusFree}>
              <div style={styles.deliveryStatusTitle}>Manual delivery on the Free plan</div>
              <div style={styles.deliveryStatusSub}>Generate your brief anytime from the Brief tab, once per day. Upgrade to get it delivered automatically every morning.</div>
            </div>
          )}
        </div>

        {/* Feedback */}
        {success && <div style={styles.successMsg}>{success}</div>}
        {error && <div style={styles.errorMsg}>{error}</div>}

        {/* Actions */}
        <div style={styles.actions}>
          <button
            className="btn btn-primary"
            style={styles.saveBtn}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            className="btn btn-ghost"
            style={styles.signOutBtn}
            onClick={onLogout}
          >
            Sign Out
          </button>
        </div>

      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    padding: '40px 24px',
  },
  container: {
    maxWidth: 640,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  heroCard: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 20,
    padding: '28px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    flexWrap: 'wrap',
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #00d4ff22, #7b61ff22)',
    border: '2px solid #00d4ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 26,
    fontWeight: 700,
    color: '#00d4ff',
    flexShrink: 0,
    fontFamily: 'Space Grotesk, sans-serif',
    boxShadow: '0 0 20px #00d4ff22',
  },
  identity: {
    flex: 1,
    minWidth: 160,
  },
  displayName: {
    fontSize: 20,
    fontWeight: 700,
    color: 'var(--text)',
    letterSpacing: '-0.02em',
  },
  email: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    marginTop: 2,
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    flexWrap: 'wrap',
  },
  memberSince: {
    fontSize: 11,
    color: 'var(--text-dim)',
  },
  dot: {
    color: 'var(--text-dim)',
    fontSize: 11,
  },
  statPill: {
    fontSize: 11,
    color: 'var(--text-secondary)',
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '2px 8px',
  },
  planBadge: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.1em',
    padding: '4px 10px',
    borderRadius: 20,
    alignSelf: 'flex-start',
  },
  planBadgeFree: {
    background: 'rgba(107, 114, 128, 0.15)',
    color: '#6b7280',
    border: '1px solid rgba(107, 114, 128, 0.3)',
  },
  planBadgePremium: {
    background: 'rgba(0, 212, 255, 0.12)',
    color: '#00d4ff',
    border: '1px solid rgba(0, 212, 255, 0.3)',
  },
  upgradeCard: {
    background: 'linear-gradient(135deg, rgba(123, 97, 255, 0.1), rgba(0, 212, 255, 0.08))',
    border: '1px solid rgba(123, 97, 255, 0.3)',
    borderRadius: 16,
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  upgradeLeft: {
    flex: 1,
  },
  upgradeTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--text)',
  },
  upgradeSub: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    marginTop: 4,
  },
  upgradeBtn: {
    fontSize: 13,
    padding: '10px 20px',
    flexShrink: 0,
  },
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--text)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  cardSub: {
    fontSize: 12,
    color: 'var(--text-dim)',
    marginTop: -8,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  input: {
    fontFamily: 'Space Grotesk, sans-serif',
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    color: 'var(--text)',
    padding: '12px 16px',
    fontSize: 14,
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
  },
  inputDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  toggleLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text)',
  },
  toggleSub: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    marginTop: 2,
  },
  toggle: {
    width: 48,
    height: 26,
    borderRadius: 13,
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    cursor: 'pointer',
    position: 'relative',
    padding: 0,
    flexShrink: 0,
    transition: 'background 0.2s, border-color 0.2s',
  },
  toggleOn: {
    background: 'rgba(0, 212, 255, 0.2)',
    borderColor: '#00d4ff',
  },
  toggleThumb: {
    position: 'absolute',
    top: 3,
    left: 3,
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: '#6b7280',
    transition: 'left 0.2s, background 0.2s',
  },
  toggleThumbOn: {
    left: 25,
    background: '#00d4ff',
  },
  deliveryStatus: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    background: 'rgba(0, 212, 255, 0.06)',
    border: '1px solid rgba(0, 212, 255, 0.2)',
    borderRadius: 12,
    padding: '16px 18px',
  },
  deliveryStatusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#00d4ff',
    boxShadow: '0 0 8px #00d4ff',
    marginTop: 5,
    flexShrink: 0,
  },
  deliveryStatusFree: {
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '16px 18px',
  },
  deliveryStatusTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--text)',
  },
  deliveryStatusSub: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    marginTop: 4,
    lineHeight: 1.6,
  },
  successMsg: {
    background: 'rgba(0, 255, 170, 0.08)',
    border: '1px solid rgba(0, 255, 170, 0.25)',
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: 13,
    color: 'var(--success)',
  },
  errorMsg: {
    background: 'rgba(255, 77, 109, 0.1)',
    border: '1px solid rgba(255, 77, 109, 0.3)',
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: 13,
    color: 'var(--danger)',
  },
  cancelCard: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  cancelTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text)',
  },
  cancelSub: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    marginTop: 4,
  },
  cancelBtn: {
    fontSize: 12,
    padding: '8px 16px',
    flexShrink: 0,
    borderColor: 'rgba(255, 77, 109, 0.4)',
    color: 'var(--danger)',
  },
  actions: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  saveBtn: {
    flex: 1,
    minWidth: 140,
  },
  signOutBtn: {
    flex: 1,
    minWidth: 120,
  },
};

export default ProfileScreen;
